import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChipIcon,
  CollectionIcon,
  DesktopComputerIcon,
  ExternalLinkIcon,
  TerminalIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import { ArrowSmRightIcon, CreditCardIcon, HomeIcon } from '@heroicons/react/solid';
import Collapse from '@material-ui/core/Collapse';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import dayjs from 'dayjs';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';

import BreadCrumb from '../components/BreadCrumb';
import CreditConsumption from '../components/CreditConsumption';
import { userActions } from '../store/user';
import ajaxPromise from '../utils/ajaxPromise';
import { MonthNames } from '../utils/constants';

const actions = [
  {
    title: 'AI-powered Prospecting',
    href: '/prospector',
    detail:
      'Use the first AI-powered business search for prospecting teams to discover your ideal leads/accounts, complete with 70+ attributes',
    icon: UserGroupIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
  },
  {
    title: 'Enrich a Contact or Company',
    href: '/enrich',
    detail: 'Turn any email or domain into a full contact or company profile',
    icon: UsersIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
  },
  {
    title: 'Bulk CSV Enrichment via Exact Match',
    href: '/exact-match',
    detail:
      'Upload a CSV to bulk enrich a list of emails, domains or phone numbers using Exact Match',
    icon: CollectionIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
  },
  {
    title: 'ExactBuyer Chrome Extension',
    href: 'https://chrome.google.com/webstore/detail/exactbuyer-realtime-conta/kpijffcodpolapmmlifhipnnnodbcgaj',
    detail:
      'Find emails, phones and company information from anywhere on the web, with just one click. Install now to get 10 bonus credits.',
    icon: DesktopComputerIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
  },
  {
    title: 'Salesforce and HubSpot Integrations',
    href: '/integrations',
    detail: 'Push contacts/companies to your HubSpot or Salesforce CRM with just one click.',
    icon: ChipIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
  },
  {
    title: 'ExactBuyer API',
    detail: 'Integrate B2B data intelligence into your product & workflows using ExactBuyer APIs',
    href: '/api-access',
    icon: TerminalIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Landing() {
  const { user } = useSelector((state) => state.user);
  const [loadingUsageStats, setLoadingUsageStats] = React.useState(false);
  const [usageStats, setUsageStats] = React.useState(false);
  const [expandGettingStarted, setExpandGettingStarted] = React.useState(true);
  const dispatch = useDispatch();
  const router = useRouter();

  const handleCollapseClicked = () => {
    const newVal = !expandGettingStarted;
    setExpandGettingStarted(newVal);
    localStorage.setItem('expandGettingStarted', newVal);
  };

  React.useEffect(() => {
    setLoadingUsageStats(true);
    ajaxPromise('/api/usage_stats', 'GET')
      .then((response) => {
        setUsageStats(response.data);
        dispatch(userActions.updateOrganizationData(response.data));
      })
      .catch(() => {})
      .then(() => {
        setLoadingUsageStats(false);
      });
    setExpandGettingStarted(localStorage.getItem('expandGettingStarted') !== 'false');
  }, []);

  const [tab, setTab] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const showCreditConsumption = router?.query?.credit_consumption;
  React.useEffect(() => {
    if (showCreditConsumption) {
      setTab(1);
    }
  }, [router.isReady]);
  return (
    <div className='py-6 min-h-screen md:mx-5'>
      <Head>
        <title>Dashboard | ExactBuyer</title>
      </Head>
      <BreadCrumb
        data={{
          currentPage: null,
          previousPages: [],
        }}
      />
      <div className='flex justify-center'>
        <div className='max-w-4xl mx-auto sm:px-6 lg:max-w-3xl'>
          <div className='flex lg:col-start-1 lg:col-span-2 '>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className='grow-1'
            >
              <Tab
                label='Getting Started'
                style={{
                  fontFamily: ['Inter', 'sans-serif'],
                  textTransform: 'inherit',
                }}
                className='focus:outline-none'
                icon={<HomeIcon className='h-5 w-5' />}
              />
              <Tab
                label='Credits Consumption'
                style={{
                  fontFamily: ['Inter', 'sans-serif'],
                  textTransform: 'inherit',
                }}
                className='focus:outline-none'
                icon={<CreditCardIcon className='h-5 w-5' />}
              />
            </Tabs>
          </div>
        </div>
      </div>

      {tab === 0 && (
        <div className='px-4 sm:px-6 md:px-0'>
          <div className='mt-2'>
            <div className='h-196 px-4'>
              <div>
                <div className='flex space-x-3 content-center'>
                  <h3 className='text-lg font-medium text-gray-900'>Get Started</h3>
                  <div
                    className='rounded-full border border-gray-400 w-7 h-7 flex items-center justify-center transition duration-300'
                    onClick={() => {
                      handleCollapseClicked();
                    }}
                  >
                    {expandGettingStarted ? (
                      <ChevronUpIcon className='h-5 w-5 text-gray-500' />
                    ) : (
                      <ChevronDownIcon className='h-5 w-5 text-gray-500' />
                    )}
                  </div>
                </div>
                <Collapse in={expandGettingStarted}>
                  <div className='mt-5'>
                    <div className='rounded-lg bg-gray-200 overflow-hidden shadow divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px'>
                      {actions.map((action, actionIdx) => (
                        <div
                          key={action.title}
                          className={classNames(
                            actionIdx === 0 ? 'rounded-tl-lg rounded-tr-lg sm:rounded-tr-none' : '',
                            actionIdx === 1 ? 'sm:rounded-tr-lg' : '',
                            actionIdx === actions.length - 2 ? 'sm:rounded-bl-lg' : '',
                            actionIdx === actions.length - 1
                              ? 'rounded-bl-lg rounded-br-lg sm:rounded-bl-none'
                              : '',
                            'relative group bg-white p-6 focus-within:ring-2 hover:shadow-lg focus-within:ring-inset focus-within:ring-indigo-500 cursor-pointer',
                          )}
                        >
                          <div className='flex justify-start'>
                            <div
                              className={classNames(
                                action.iconBackground,
                                action.iconForeground,
                                'rounded-lg h-12 p-3 mr-3 ring-4 ring-white',
                              )}
                            >
                              <action.icon className='h-6 w-6' aria-hidden='true' />
                            </div>
                            <div className=''>
                              <h3 className='text-lg font-medium'>
                                {['AI-powered Prospecting'].includes(action.title) ? (
                                  <a href={action.href} target='_blank' rel='noreferrer'>
                                    <p className='focus:outline-none flex'>
                                      {/* Extend touch target to entire panel */}
                                      <span className='absolute inset-0' aria-hidden='true' />
                                      {action.title}
                                      <div className='w-10 h-5 ml-3 mt-0.5 mr-8 py-0.5 px-1.5 rounded text-xs antialiased bg-exact-blue text-white'>
                                        New
                                      </div>
                                    </p>
                                  </a>
                                ) : (
                                  <Link href={action.href}>
                                    <p className='focus:outline-none'>
                                      {/* Extend touch target to entire panel */}
                                      <span className='absolute inset-0' aria-hidden='true' />
                                      {action.title}
                                    </p>
                                  </Link>
                                )}
                              </h3>
                              <p className='mt-2 text-sm text-gray-500'>{action.detail}</p>
                            </div>
                          </div>

                          <span
                            className='pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-indigo-400'
                            aria-hidden='true'
                          >
                            {action.title === 'ExactBuyer Chrome Extension' ? (
                              <ExternalLinkIcon className='h-6 w-6' />
                            ) : (
                              <ArrowRightIcon className='h-6 w-6' />
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Collapse>
              </div>

              <div className='mt-12'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-lg leading-6 font-medium text-gray-900'>Usage Statistics</h3>
                  <div
                    className='flex text-lg leading-6 font-medium antialiased text-gray-900 hover:text-exact-blue cursor-pointer'
                    onClick={() => setTab(1)}
                  >
                    View Detailed Stats
                    <ArrowSmRightIcon className='h-6 w-6' />
                  </div>
                </div>

                <dl
                  className={`mt-5 grid grid-cols-1 gap-5 ${
                    user?.permission_profile?.ORGANIZATION_VIEW_ACCESS
                      ? 'lg:grid-cols-2 xl:grid-cols-4'
                      : 'lg:grid-cols-2'
                  } `}
                >
                  <div className='flex flex-col bg-white overflow-hidden shadow rounded-lg'>
                    <div className='grow px-4 py-5 sm:p-6'>
                      <div className='flex items-center'>
                        <div className='shrink-0 bg-exact-blue rounded-md p-3'>
                          <TrendingUpIcon className='text-white h-6 w-6' />
                        </div>
                        <div className='ml-5 w-0 flex-1'>
                          <dt className='text-sm font-medium text-gray-500 truncate'>
                            Personal Credits Remaining
                          </dt>
                          <dd className='flex items-baseline'>
                            {!loadingUsageStats ? (
                              <div className='text-2xl font-semibold text-gray-900'>
                                {usageStats?.credits_remaining}
                              </div>
                            ) : (
                              <Loader
                                className='m-1'
                                type='ThreeDots'
                                color='#5046E4'
                                height={24}
                                width={24}
                              />
                            )}
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='flex flex-col bg-white overflow-hidden shadow rounded-lg'>
                    <div className='grow px-4 py-5 sm:p-6'>
                      <div className='flex items-center'>
                        <div className='shrink-0 bg-exact-blue rounded-md p-3'>
                          <TrendingDownIcon className='text-white h-6 w-6' />
                        </div>
                        <div className='ml-5 w-0 flex-1'>
                          <dt className='text-sm font-medium text-gray-500 truncate'>
                            Personal Credits Consumed - {MonthNames[dayjs().month()]}
                          </dt>
                          <dd className='flex items-baseline'>
                            {!loadingUsageStats ? (
                              <div className='text-2xl font-semibold text-gray-900'>
                                {usageStats?.credits_consumed_this_month}
                              </div>
                            ) : (
                              <Loader
                                className='m-1'
                                type='ThreeDots'
                                color='#5046E4'
                                height={24}
                                width={24}
                              />
                            )}
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>
                  {user?.is_team_member && user?.permission_profile?.ORGANIZATION_VIEW_ACCESS && (
                    <>
                      <div className='flex flex-col bg-white overflow-hidden shadow rounded-lg'>
                        <div className='grow px-4 py-5 sm:p-6'>
                          <div className='flex items-center'>
                            <div className='shrink-0 bg-exact-blue rounded-md p-3'>
                              <TrendingUpIcon className='text-white h-6 w-6' />
                            </div>
                            <div className='ml-5 w-0 flex-1'>
                              <dt className='text-sm font-medium text-gray-500 truncate'>
                                Team Credits Remaining
                              </dt>
                              <dd className='flex items-baseline text-2xl font-semibold text-gray-900'>
                                {!loadingUsageStats ? (
                                  <div className='text-2xl font-semibold text-gray-900'>
                                    {usageStats?.team_total_remaining_credits}
                                  </div>
                                ) : (
                                  <Loader
                                    className='m-1'
                                    type='ThreeDots'
                                    color='#5046E4'
                                    height={24}
                                    width={24}
                                  />
                                )}
                              </dd>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col bg-white overflow-hidden shadow rounded-lg'>
                        <div className='grow px-4 py-5 sm:p-6'>
                          <div className='flex items-center'>
                            <div className='shrink-0 bg-exact-blue rounded-md p-3'>
                              <TrendingDownIcon className='text-white h-6 w-6' />
                            </div>
                            <div className='ml-5 w-0 flex-1'>
                              <dt className='text-sm font-medium text-gray-500 truncate'>
                                Team Credits Consumed - {MonthNames[dayjs().month()]}
                              </dt>
                              <dd className='flex items-baseline text-2xl font-semibold text-gray-900'>
                                {!loadingUsageStats ? (
                                  <div className='text-2xl font-semibold text-gray-900'>
                                    {usageStats?.team_credits_consumed_this_month}
                                  </div>
                                ) : (
                                  <Loader
                                    className='m-1'
                                    type='ThreeDots'
                                    color='#5046E4'
                                    height={24}
                                    width={24}
                                  />
                                )}
                              </dd>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </dl>
              </div>

              <div className='mt-12'>
                <div className='flex justify-between'>
                  <h3 className='text-lg leading-6 font-medium text-gray-900'>Current Plan</h3>
                </div>

                <dl className='mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:2'>
                  <div className='bg-white shadow rounded-lg'>
                    <div className='flex px-5 pt-5 justify-between content-center'>
                      <h3 className='text-lg font-medium text-gray-900'>Subscription</h3>
                      <button
                        type='button'
                        className='px-2 py-1 border border-transparent bg-exact-blue hover:bg-blue-900 shadow-sm text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                        onClick={() => {
                          router.push('/pricing');
                        }}
                      >
                        Change Plan
                      </button>
                    </div>
                    <table className='table-auto mx-6 mt-2 mb-5 text-gray-600  text-sm'>
                      <tbody>
                        <tr>
                          <td className='pr-10'>Plan</td>
                          <td className='flex space-x-4'>
                            {user?.subscription?.product?.name || 'Free Plan '}
                            {user?.free_trial?.trial_active && (
                              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-exact-blue ml-2'>
                                Free Trial Active
                              </span>
                            )}{' '}
                          </td>
                        </tr>
                        <tr>
                          <td className='pr-10'>Next Credits Cycle</td>
                          <td>
                            {user?.subscription?.recurring_date ||
                              user?.subscription?.current_period_end ||
                              '-'}
                          </td>
                        </tr>
                        <tr>
                          <td className='pr-10'>
                            {user?.subscription ? 'Credits per year' : 'Credits per month'}
                          </td>
                          <td>
                            {user?.subscription?.plan_recurring_credits ||
                              user?.subscription?.product?.metadata?.yearly_credits ||
                              '15'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className='bg-white shadow rounded-lg'>
                    <div className='flex px-5 pt-5 justify-between content-center'>
                      <h3 className='text-lg font-medium text-gray-900'>Team Members</h3>
                      {user?.is_team_member ? (
                        <button
                          type='button'
                          className='px-2 py-1 border border-transparent bg-exact-blue hover:bg-blue-900 shadow-sm text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                          onClick={() => {
                            router.push('/organization');
                          }}
                        >
                          Manage Team
                        </button>
                      ) : (
                        <button
                          type='button'
                          className='px-2 py-1 border border-transparent bg-exact-blue hover:bg-blue-900 shadow-sm text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                          onClick={() => {
                            router.push('/pricing');
                          }}
                        >
                          Upgrade plan
                        </button>
                      )}
                      {user?.is_team_member}
                    </div>
                    <table className='table-auto mx-6 mt-2 mb-5 text-gray-600 text-sm'>
                      <tbody>
                        <tr>
                          <td className='pr-10'>Current Team Members</td>
                          <td>{user?.subscription?.organization_active_users || 1}</td>
                        </tr>
                        <tr>
                          <td className='pr-10'>Available Seats</td>
                          <td>
                            {user?.subscription?.seats -
                              user?.subscription?.organization_active_users || 0}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 1 && (
        <div className='px-4 sm:px-6 md:px-0'>
          <CreditConsumption />
        </div>
      )}
    </div>
  );
}
